/**
 * CSS виджета.
 *
 * Все цвета, радиусы и размеры управляются через CSS-переменные (--mid-*).
 * Переменные выставляются на корневом элементе .mid-widget из конфига темы —
 * см. MobileIDWidget.prototype._applyTheme в widget.js.
 *
 * Благодаря этому подходу:
 *  - CSS инжектируется один раз на страницу (независимо от числа виджетов);
 *  - каждый экземпляр виджета имеет свои переменные на своём корневом элементе;
 *  - тему можно переопределить и через внешний CSS.
 */
export var CSS = `
/* ─── Корень ────────────────────────────────────────────────────────────── */
.mid-widget {
  /* Сброс наследуемых свойств хост-страницы */
  font-family:    var(--mid-font, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif);
  font-size:      var(--mid-font-size, 16px);
  font-style:     normal;
  font-weight:    normal;
  line-height:    1.5;
  letter-spacing: normal;
  word-spacing:   normal;
  text-align:     left;
  text-transform: none;
  color:          var(--mid-input-text, #111827);
  /* Геометрия */
  background:     var(--mid-bg, #fff);
  box-sizing:     border-box;
}
.mid-widget *,
.mid-widget *::before,
.mid-widget *::after {
  box-sizing: border-box;
}
/* Форм-элементы не наследуют шрифт автоматически — выравниваем явно */
.mid-widget input,
.mid-widget button {
  font-family: inherit;
  font-size:   inherit;
  line-height: inherit;
  color:       inherit;
}

/* ─── Экраны ────────────────────────────────────────────────────────────── */
.mid-widget .mid-screen        { display: none; }
.mid-widget .mid-screen--active { display: block; }

/* ─── Подпись (label) ───────────────────────────────────────────────────── */
.mid-widget .mid-label {
  display: block;
  margin-bottom: var(--mid-gap-label, 6px);
  color: var(--mid-label, #374151);
  font-size: 0.875em;
  font-weight: 500;
}

/* ─── Поле ввода ────────────────────────────────────────────────────────── */
.mid-widget .mid-input {
  display: block;
  width: 100%;
  padding: var(--mid-padding, 12px);
  background: var(--mid-input-bg, #f9fafb);
  border: 1.5px solid var(--mid-input-border, #d1d5db);
  border-radius: var(--mid-radius, 8px);
  color: var(--mid-input-text, #111827);
  font-size: var(--mid-font-size, 16px);
  font-family: var(--mid-font, inherit);
  outline: none;
  transition: border-color 0.15s;
  -webkit-appearance: none;
  -moz-appearance: textfield;
}
.mid-widget .mid-input::-webkit-outer-spin-button,
.mid-widget .mid-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.mid-widget .mid-input:focus {
  border-color: var(--mid-input-border-focus, #2563eb);
}
.mid-widget .mid-input.mid-input--error {
  border-color: var(--mid-error, #ef4444);
}

/* ─── Сообщение об ошибке ───────────────────────────────────────────────── */
.mid-widget .mid-error {
  display: block;
  min-height: 1.3em;
  margin-top: 5px;
  color: var(--mid-error, #ef4444);
  font-size: 0.8em;
}

/* ─── Кнопка ────────────────────────────────────────────────────────────── */
.mid-widget .mid-btn {
  display: block;
  width: var(--mid-btn-width, 100%);
  padding: var(--mid-padding, 12px);
  margin-top: var(--mid-gap, 10px);
  border: none;
  border-radius: var(--mid-btn-radius, 8px);
  cursor: pointer;
  font-size: var(--mid-font-size, 16px);
  font-family: var(--mid-font, inherit);
  font-weight: 500;
  line-height: 1.25;
  transition: background 0.15s, opacity 0.15s;
  -webkit-appearance: none;
}
.mid-widget .mid-btn--primary {
  background: var(--mid-primary, #2563eb);
  color: var(--mid-primary-text, #fff);
}
.mid-widget .mid-btn--primary:hover:not(:disabled) {
  background: var(--mid-primary-hover, #1d4ed8);
}
.mid-widget .mid-btn--primary:disabled {
  opacity: 0.6;
  cursor: default;
}
.mid-widget .mid-btn--ghost {
  background: transparent;
  color: var(--mid-hint, #6b7280);
  font-size: 0.875em;
  font-weight: 400;
  padding: 6px;
  margin-top: calc(var(--mid-gap, 10px) / 2);
}
.mid-widget .mid-btn--ghost:hover {
  color: var(--mid-input-text, #111827);
}

/* ─── Загрузка / Ожидание ───────────────────────────────────────────────── */
.mid-widget .mid-screen--loading,
.mid-widget .mid-screen--pending {
  text-align: center;
  padding: 24px 0;
}
.mid-widget .mid-spinner {
  display: inline-block;
  width: 32px;
  height: 32px;
  border: 3px solid var(--mid-input-border, #d1d5db);
  border-top-color: var(--mid-spinner, #2563eb);
  border-radius: 50%;
  animation: mid-spin 0.7s linear infinite;
  margin-bottom: 10px;
}
@keyframes mid-spin { to { transform: rotate(360deg); } }
.mid-widget .mid-pending-text {
  display: block;
  color: var(--mid-hint, #6b7280);
  font-size: 0.9em;
}

/* ─── Финальные экраны ──────────────────────────────────────────────────── */
.mid-widget .mid-screen--verified,
.mid-widget .mid-screen--rejected,
.mid-widget .mid-screen--rate-limit {
  text-align: center;
}

.mid-widget .mid-screen--verified .mid-label,
.mid-widget .mid-screen--rejected .mid-label,
.mid-widget .mid-screen--rate-limit .mid-label {
  text-align: left;
}

/* Вариант 1: текст */
.mid-widget .mid-screen--verified:not(.mid-screen--result-phone) {
  color: var(--mid-success, #16a34a);
}

/*
 * Вариант 2: resultView = 'phone'
 * Структура экрана зеркалит экран ввода: label → input → статус.
 * Элементы не смещаются при переходе между экранами.
 *
 *  .mid-label            — та же подпись, что и на экране ввода
 *  .mid-input--result    — задизейбленное поле с номером
 *  .mid-result-status    — строка статуса ниже поля (шрифт = .mid-error)
 */
.mid-widget .mid-input--result {
  cursor: default;
}
.mid-widget .mid-input--result:disabled {
  opacity: 1; /* убираем браузерное приглушение disabled-полей */
}
.mid-widget .mid-input--result-verified { border-color: var(--mid-success,  #16a34a); }
.mid-widget .mid-input--result-rejected { border-color: var(--mid-rejected, #ef4444); }

.mid-widget .mid-result-status {
  display: block;
  min-height: 1.3em;
  margin-top: 5px;
  font-size: 0.8em; /* совпадает с .mid-error */
}
.mid-widget .mid-result-status--verified { 
  color: var(--mid-success,  #16a34a); 
  text-align: left;
}
.mid-widget .mid-result-status--rejected { 
  color: var(--mid-rejected, #ef4444); 
  text-align: left;
}

.mid-widget .mid-rejected-title {
  font-weight: 500;
  font-size: 1.05em;
  color: var(--mid-rejected, #ef4444);
  margin-bottom: var(--mid-gap, 10px);
}
.mid-widget .mid-btn--retry {
  margin-top: 0;
}
`;
